import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, Text } from '@chakra-ui/react';
import { motion, useAnimation, useMotionValue } from 'framer-motion';

const MobileHashtagsCarousel = ({ hashtags, onClick }) => {
    const containerRef = useRef(null);
    const contentRef = useRef(null);
    const [constraints, setConstraints] = useState({ left: 0, right: 0 });
    const controls = useAnimation();
    const x = useMotionValue(0);
    const isInteracting = useRef(false);
    const lastDragTime = useRef(0);

    // Obliczamy granice przeciągania
    useEffect(() => {
        const calculateConstraints = () => {
            if (contentRef.current && containerRef.current) {
                const contentWidth = contentRef.current.scrollWidth;
                const containerWidth = containerRef.current.offsetWidth;
                // Dodajemy margines (padding-right) do granic
                const maxDrag = Math.max(0, contentWidth - containerWidth + 32);
                setConstraints({ left: -maxDrag, right: 0 });
            }
        };

        calculateConstraints();
        // Ponawiamy przy zmianie rozmiaru okna
        window.addEventListener('resize', calculateConstraints);
        return () => window.removeEventListener('resize', calculateConstraints);
    }, [hashtags]);

    // Logika skokowego przewijania (kompromis)
    useEffect(() => {
        const interval = setInterval(() => {
            // Nie skaczemy, jeśli użytkownik właśnie dotykał slidera
            if (isInteracting.current || (Date.now() - lastDragTime.current < 1500)) return;

            const currentXVal = -x.get();
            const buttons = Array.from(contentRef.current?.querySelectorAll('button') || []);
            
            if (buttons.length === 0) return;

            // Znajdujemy następny hashtag, który zaczyna się po obecnej pozycji lewej krawędzi
            const nextButton = buttons.find(btn => btn.offsetLeft > currentXVal + 10);
            
            let nextX = nextButton ? -nextButton.offsetLeft : 0;

            // Jeśli doszliśmy do końca (lub blisko końca), wracamy na początek
            if (!nextButton || nextX < (constraints.left - 10)) {
                nextX = 0;
            }

            controls.start({
                x: nextX,
                transition: { duration: 0.8, ease: "easeInOut" }
            });
        }, 2500);

        return () => clearInterval(interval);
    }, [constraints.left, controls, x]);

    if (!hashtags || hashtags.length === 0) return null;

    return (
        <Box
            ref={containerRef}
            w="100%"
            overflow="hidden"
            mb="16px"
            position="relative"
            onPointerDown={() => {
                isInteracting.current = true;
                stopMomentum();
            }}
            onPointerUp={() => {
                isInteracting.current = false;
                lastDragTime.current = Date.now();
            }}
            sx={{
                WebkitTapHighlightColor: 'transparent',
                touchAction: 'pan-y pinch-zoom'
            }}
        >
            <motion.div
                ref={contentRef}
                drag="x"
                dragConstraints={constraints}
                dragElastic={0.1}
                dragTransition={{ 
                    power: 0.2, 
                    timeConstant: 200, 
                    modifyTarget: target => {
                        const buttons = Array.from(contentRef.current?.querySelectorAll('button') || []);
                        if (buttons.length === 0) return target;
                        // Znajdujemy najbliższy punkt początku hashtaga dla celu inercji
                        const nearest = buttons.reduce((prev, curr) => 
                            Math.abs(-curr.offsetLeft - target) < Math.abs(-prev.offsetLeft - target) ? curr : prev
                        );
                        return -nearest.offsetLeft;
                    }
                }}
                animate={controls}
                style={{ 
                    x,
                    display: 'flex',
                    gap: '8px',
                    width: 'max-content',
                    padding: '4px 16px 4px 0',
                    cursor: 'grab',
                    touchAction: 'pan-y pinch-zoom'
                }}
                onDragStart={() => {
                    isInteracting.current = true;
                }}
                onDragEnd={() => {
                    isInteracting.current = false;
                    lastDragTime.current = Date.now();
                }}
                whileTap={{ cursor: 'grabbing' }}
            >
                {hashtags.map((hashtag) => (
                    <Button
                        key={hashtag.id}
                        bg={'surface.card'}
                        border={'1px solid'}
                        borderColor={'brand.200'}
                        px={'16px'}
                        h={'44px'}
                        borderRadius={'full'}
                        w={'auto'}
                        flexShrink={0}
                        _active={{ bg: 'brand.100' }}
                        // Framer Motion sam obsłuży to, aby onClick nie odpalił się przy dragu
                        onClick={() => onClick(hashtag)}
                    >
                        <Text fontWeight={'600'} fontSize='13px' color={'brand.700'} pointerEvents="none">
                            #{hashtag.name.replace(/^#+/, '')}
                        </Text>
                    </Button>
                ))}
            </motion.div>
        </Box>
    );
};

// Funkcja pomocnicza do zatrzymania animacji sterowanej przez controls
const stopMomentum = () => {
    // useAnimation nie ma prostej metody stop(), ale wywołanie start z bieżącą pozycją działo podobnie
};

export default MobileHashtagsCarousel;
